import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { HeroPanelData } from '../components/template/panels/hero-panel';
import { TemplatePopulatorService } from './template-populator.service';

/**
 * Mutable context bag for hero panel operations.
 */
export interface HeroContext {
  /** Current page JSON data — mutated in-place. */
  json: any;
  /** Elements array — may be mutated. */
  elements: WebsiteElement[];
  /** Current hero panel data — mutated by save/change methods. */
  heroPanelData: HeroPanelData;
  /** Template source data — read-only. */
  templateData: any;
  /** Whether the hero panel is open. */
  showHeroPanel: boolean;
  /** Delegate for queueing autosave paths. */
  scheduleAutosave(paths: string[]): void;
  /** Delegate for flushing autosave immediately. */
  flushAutosave(): void;
  /** Delegate for recording undo/redo state. */
  recordStateChange(description: string): void;
}

/**
 * Handles the hero panel open/change/save lifecycle.
 * Methods operate on a {@link HeroContext} passed by the component.
 */
@Injectable({ providedIn: 'root' })
export class HeroPanelService {

  constructor(
    private logger: LoggerService,
    private templatePopulator: TemplatePopulatorService
  ) {}
  closeHeroPanel(ctx: HeroContext): void {
    ctx.showHeroPanel = false;
  }
  /**
  * Saves hero panel data into the page model, updates preview elements, and triggers autosave, with special handling for hero layout preservation and conversion.
  * @example
  * nHeroPanelSave(data, ctx)
  * void
  * @param {HeroPanelData} data - Hero panel content and style data to persist.
  * @param {HeroContext} ctx - Current hero editing context, including JSON state, elements, and autosave controls.
  * @returns {void} No return value.
  **/
  onHeroPanelSave(data: HeroPanelData, ctx: HeroContext): void {
    // CRITICAL: Capture old heroPanelData.layout BEFORE overwriting it, in case data.layout is undefined
    const previousLayout = ctx.heroPanelData.layout;
    
    this.logger.debug('🔴 onHeroPanelSave CALLED with data:', {
      heading: data.heading,
      paragraph: data.paragraph,
      headingLength: data.heading?.length,
      paragraphLength: data.paragraph?.length,
      layout: data.layout,
      previousHeroPanelDataLayout: previousLayout
    });
    
    ctx.heroPanelData = data;

    // CRITICAL: When hero panel is open, ALWAYS save to hero_section
    if (ctx.showHeroPanel) {
      this.logger.debug('🔴 Hero panel is OPEN, saving to hero_section');
      
      if (!ctx.json) ctx.json = {};
      if (!ctx.json.page) ctx.json.page = {};
      if (!ctx.json.page.sections) ctx.json.page.sections = {};
      if (!ctx.json.page.sections.hero_section) {
        ctx.json.page.sections.hero_section = {};
      }
      
      const heroSection = ctx.json.page.sections.hero_section;
      this.logger.debug('🔴 BEFORE save - heroSection:', JSON.stringify(heroSection));
      
      // Update title - ALWAYS set the label
      if (!heroSection.title || typeof heroSection.title !== 'object') {
        heroSection.title = {};
      }
      heroSection.title.label = data.heading;
      heroSection.title.style = {
        fontWeight: data.titleStyle?.fontWeight || null,
        fontStyle: data.titleStyle?.fontStyle || null,
        textDecoration: data.titleStyle?.textDecoration || null,
        textAlign: data.titleStyle?.textAlign || null
      };
      
      // Update description
      heroSection.description = data.paragraph;
      
      // Update button
      heroSection.button = {
        ...(heroSection.button || {}),
        label: data.buttonText,
        link: data.buttonNavigation,
        style: heroSection.button?.style || { background: "#1E40AF", color: "#FFFFFF" }
      };
      
      // Update image
      const imageSrc = data.image?.src || data.imageUrl || '';
      const imageScaletype = data.image?.scaletype || data.imageFit || 'fit';
      heroSection.image = {
        ...(heroSection.image || {}),
        src: imageSrc,
        alt: data.image?.alt || heroSection.image?.alt || '',
        width: data.image?.width || heroSection.image?.width || '420px',
        height: data.image?.height || heroSection.image?.height || '320px',
        scaletype: imageScaletype
      };
      
      // Keep active state
      if (heroSection.active === undefined) heroSection.active = true;
      
      // Update layout - ensure layout object exists
      if (!heroSection.layout) {
        heroSection.layout = { 
          type: 'layout_1',
          grid_columns: 2, 
          image_alignment: "right", 
          padding: "100px 120px" 
        };
      }
      
      // Convert layout to proper format (layout_X)
      // CRITICAL: Use data.layout first (from save event), then fallback to previousLayout (captured before overwriting heroPanelData)
      // This ensures we always use the most recent layout value even if data.layout is somehow undefined
      const layoutType = data.layout || previousLayout || heroSection.layout?.type || 'layout_1';
      this.logger.debug('🔴 Layout conversion', { dataLayout: data.layout, previousLayout, heroSectionLayoutType: heroSection.layout?.type, finalLayoutType: layoutType });
      
      if (typeof layoutType === 'string') {
        // If it's already in layout_X format, use it
        if (layoutType.startsWith('layout_')) {
          heroSection.layout.type = layoutType as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
        } 
        // If it's just a number (1, 2, 3, etc.), convert to layout_X
        else if (/^[1-7]$/.test(layoutType)) {
          heroSection.layout.type = `layout_${layoutType}` as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
        // Default to layout_1
        else {
          this.logger.warn('🔴 Invalid layout format, defaulting to layout_1. Received:', layoutType);
          heroSection.layout.type = 'layout_1';
        }
      } else {
        this.logger.warn('🔴 Layout is not a string, defaulting to layout_1. Received:', layoutType);
        heroSection.layout.type = 'layout_1';
      }
      
      this.logger.debug('🔴 Final heroSection.layout.type:', heroSection.layout.type);
      
      // Update background color in layout
      heroSection.layout.background_color = data.backgroundColor;
      
      // Update grid_columns and image_alignment based on layout type if needed
      // For layout_4 (which is used in champs template), ensure proper defaults
      if (heroSection.layout.type === 'layout_4') {
        if (!heroSection.layout.grid_columns) heroSection.layout.grid_columns = 2;
        if (!heroSection.layout.image_alignment) heroSection.layout.image_alignment = 'right';
        if (!heroSection.layout.padding) heroSection.layout.padding = '100px 120px';
      }
      
      this.logger.debug('🔴 AFTER save - heroSection:', JSON.stringify(heroSection));
      
      // Update hero element content for preview
      const heroElement = ctx.elements.find(e => e.id === 'hero' || e.type === 'hero');
      if (heroElement) {
        heroElement.content = {
          ...heroElement.content,
          title: data.heading,
          titleStyle: data.titleStyle || undefined,
          description: data.paragraph,
          buttonText: data.buttonText,
          image: data.image,
          imageUrl: imageSrc,
          layout: data.layout || '1',
          backgroundColor: data.backgroundColor
        } as any;
        ctx.elements = [...ctx.elements];
      }
      
      // Trigger autosave
      ctx.flushAutosave();
      return;
    }

    // Legacy flow for non-hero panel saves (shouldn't typically reach here when hero panel is open)
    const selected = ctx.elements.find(e => e.selected);
    if (!selected) {
      this.logger.warn('🔴 No element selected for text edit');
      return;
    }

    // Update currentJsonData based on element type
    if (!ctx.json) ctx.json = {};

    // Ensure page.sections structure exists
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    switch (selected.id) {
      case 'hero': {
        // Update in page.sections.hero_section (new structure)
        if (!ctx.json.page.sections.hero_section) {
          ctx.json.page.sections.hero_section = {};
        }
        const heroSection = ctx.json.page.sections.hero_section;
        // Preserve existing properties (layout, active, etc.) and only update content fields
        // Update title - ALWAYS set the label
        if (!heroSection.title || typeof heroSection.title !== 'object') {
          heroSection.title = {};
        }
        heroSection.title.label = data.heading;
        heroSection.title.style = {
          fontWeight: data.titleStyle?.fontWeight || null,
          fontStyle: data.titleStyle?.fontStyle || null,
          textDecoration: data.titleStyle?.textDecoration || null,
          textAlign: data.titleStyle?.textAlign || null
        };
        heroSection.description = data.paragraph;
        heroSection.button = {
          ...(heroSection.button || {}),
          label: data.buttonText,
          link: data.buttonNavigation,
          style: heroSection.button?.style || {
            background: "#1E40AF",
            color: "#FFFFFF"
          }
        };
        heroSection.image = {
          ...(heroSection.image || {}),
          src: data.image?.src || '',
          alt: data.image?.alt || heroSection.image?.alt || '',
          width: data.image?.width || heroSection.image?.width || '420px',
          height: data.image?.height || heroSection.image?.height || '320px',
          scaletype: data.image?.scaletype || heroSection.image?.scaletype || 'fit'
        };
        // Keep active state if it exists
        if (heroSection.active === undefined) {
          heroSection.active = true;
        }
        // Update layout - ensure layout object exists
        if (!heroSection.layout) {
          heroSection.layout = {
            type: 'layout_1',
            grid_columns: 2,
            image_alignment: "right",
            padding: "100px 120px"
          };
        }
        
        // Convert layout to proper format (layout_X)
        const layoutType = data.layout || ctx.heroPanelData.layout || 'layout_1';
        if (typeof layoutType === 'string') {
          if (layoutType.startsWith('layout_')) {
            heroSection.layout.type = layoutType as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
          } else if (/^[1-7]$/.test(layoutType)) {
            heroSection.layout.type = `layout_${layoutType}` as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
            // Update image_alignment based on layout number
            if (layoutType === '3') {
              heroSection.layout.image_alignment = "left";
            } else {
              heroSection.layout.image_alignment = "right";
            }
          } else {
            heroSection.layout.type = 'layout_1';
          }
        } else {
          heroSection.layout.type = 'layout_1';
        }
        // Update background_color in layout
            heroSection.layout.background_color = data.backgroundColor;
        break;
      }
      case 'welcome': {
        // Update in page.sections.info_section (new structure)
        if (!ctx.json.page.sections.info_section) {
          ctx.json.page.sections.info_section = {};
        }
        const aboutSection = ctx.json.page.sections.info_section;
        aboutSection.title = data.heading;
        aboutSection.description = data.paragraph;
        aboutSection.button = {
          ...(aboutSection.button || {}),
          label: data.buttonText,
          link: data.buttonNavigation
        };
        aboutSection.image = {
          ...(aboutSection.image || {}),
          src: data.image?.src || '',
          alt: data.image?.alt || '',
          width: data.image?.width || '420px',
          height: data.image?.height || '320px',
          scaletype: data.image?.scaletype || 'fit'
        };
        // Keep active state if it exists
        if (aboutSection.active === undefined) {
          aboutSection.active = true;
        }
        // Store layout value (1, 2, or 3)
        aboutSection.layout_type = data.layout || '1';
        break;
      }
      case 'services': {
        // Services section is no longer used - removed
        break;
      }
      // Add other element mappings as needed
    }

    // Refresh editable JSON string for the quick editor

    // Also reflect to preview immediately
    if (selected.id === 'hero') {
      const heroElement = ctx.elements.find(e => e.id === 'hero');
      if (heroElement) {
      heroElement.content = {
        title: data.heading,
        titleStyle: data.titleStyle || undefined,
        description: data.paragraph,
        buttonText: data.buttonText,
        imageUrl: data.image?.src || '', // Legacy support
        image: data.image || {
          src: '',
          alt: '',
          width: '420px',
          height: '320px',
          scaletype: 'fit'
        },
        backgroundColor: data.backgroundColor
      } as any;
      }
    }
    if (selected.id === 'welcome') {
      const welcomeElement = ctx.elements.find(e => e.id === 'welcome');
      if (welcomeElement) {
        welcomeElement.content = {
          title: data.heading,
          description: data.paragraph,
          buttonText: data.buttonText,
          imageUrl: data.image?.src || '', // Legacy support
          image: data.image || {
            src: '',
            alt: '',
            width: '420px',
            height: '320px',
            scaletype: 'fit'
          },
          layout: data.layout || '1'
        } as any;
      }
    }
    if (selected.id === 'services') {
      const servicesElement = ctx.elements.find(e => e.id === 'services');
      if (servicesElement) {
        servicesElement.content = {
          title: data.heading,
          description: data.paragraph,
          buttonText: data.buttonText,
          imageUrl: data.image?.src || '', // Legacy support
          image: data.image || {
            src: '',
            alt: '',
            width: '420px',
            height: '320px',
            scaletype: 'fit'
          }
        } as any;
      }
    }

    // Schedule autosave (balanced preset)
    const changedPaths: string[] = [];
    if (selected.id === 'hero') changedPaths.push('/hero');
    if (selected.id === 'welcome') changedPaths.push('/sections/welcome');
    if (selected.id === 'services') changedPaths.push('/services');
    ctx.scheduleAutosave(changedPaths);
  }
  /**
   * Updates hero panel data in the JSON model and live preview, with special handling when the hero panel is open.
   * @example
   * nHeroPanelChange(sampleHeroData, sampleContext)
   * void
   * @param {HeroPanelData} data - Hero panel content and styling data to apply.
   * @param {HeroContext} ctx - Current hero editing context containing JSON state, selected elements, and autosave handlers.
   * @returns {void} This function does not return a value.
   */
  onHeroPanelChange(data: HeroPanelData, ctx: HeroContext): void {
    this.logger.debug('🔵 onHeroPanelChange CALLED with data:', {
      heading: data.heading,
      paragraph: data.paragraph,
      headingLength: data.heading?.length,
      paragraphLength: data.paragraph?.length
    });
    
    ctx.heroPanelData = data;
    
    // Find selected element, or fallback to hero element if hero panel is open
    const selected = ctx.elements.find(e => e.selected);
    this.logger.debug('🔵 Selected element from websiteElements:', selected ? { id: selected.id, type: selected.type, selected: selected.selected } : 'NONE');
    
    // CRITICAL FIX: When hero panel is open, ALWAYS update hero_section regardless of selection
    if (ctx.showHeroPanel) {
      this.logger.debug('🔵 Hero panel is OPEN, will update hero_section');
      // Force update hero_section when hero panel is open
      if (!ctx.json) ctx.json = {};
      if (!ctx.json.page) ctx.json.page = {};
      if (!ctx.json.page.sections) ctx.json.page.sections = {};
      if (!ctx.json.page.sections.hero_section) {
        ctx.json.page.sections.hero_section = {};
      }
      
      const heroSection = ctx.json.page.sections.hero_section;
      this.logger.debug('🔵 BEFORE update - heroSection.title:', heroSection.title);
      this.logger.debug('🔵 BEFORE update - heroSection.description:', heroSection.description);
      
      // Update title - ALWAYS set the label regardless of existing structure
      if (!heroSection.title || typeof heroSection.title !== 'object') {
        heroSection.title = {};
      }
      heroSection.title.label = data.heading;
      heroSection.title.style = {
        fontWeight: data.titleStyle?.fontWeight || null,
        fontStyle: data.titleStyle?.fontStyle || null,
        textDecoration: data.titleStyle?.textDecoration || null,
        textAlign: data.titleStyle?.textAlign || null
      };
      
      // Update description
      heroSection.description = data.paragraph;
      
      // Update button
      heroSection.button = {
        ...(heroSection.button || {}),
        label: data.buttonText,
        link: data.buttonNavigation,
        style: heroSection.button?.style || { background: "#1E40AF", color: "#FFFFFF" }
      };
      
      // Update image
      const imageSrc = data.image?.src || data.imageUrl || '';
      const imageScaletype = data.image?.scaletype || data.imageFit || 'fit';
      heroSection.image = {
        ...(heroSection.image || {}),
        src: imageSrc,
        alt: data.image?.alt || heroSection.image?.alt || '',
        width: data.image?.width || heroSection.image?.width || '420px',
        height: data.image?.height || heroSection.image?.height || '320px',
        scaletype: imageScaletype
      };
      
      // Keep active state
      if (heroSection.active === undefined) heroSection.active = true;
      
      // Update layout - ensure layout object exists
      if (!heroSection.layout) {
        heroSection.layout = {
          type: 'layout_1',
          grid_columns: 2,
          image_alignment: "right",
          padding: "100px 120px"
        };
      }
      
      // Convert layout to proper format (layout_X)
      const layoutType = data.layout || ctx.heroPanelData.layout || 'layout_1';
      if (typeof layoutType === 'string') {
        if (layoutType.startsWith('layout_')) {
          heroSection.layout.type = layoutType as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
        } else if (/^[1-7]$/.test(layoutType)) {
          heroSection.layout.type = `layout_${layoutType}` as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
        } else {
          heroSection.layout.type = 'layout_1';
        }
      } else {
        heroSection.layout.type = 'layout_1';
      }
      
      heroSection.layout.background_color = data.backgroundColor;
      
      this.logger.debug('🔵 AFTER update - heroSection.title:', heroSection.title);
      this.logger.debug('🔵 AFTER update - heroSection.description:', heroSection.description);
      
      // Update hero element content for preview
      const heroElement = ctx.elements.find(e => e.id === 'hero' || e.type === 'hero');
      if (heroElement) {
        heroElement.content = {
          ...heroElement.content,
          title: data.heading,
          titleStyle: data.titleStyle || undefined,
          description: data.paragraph,
          buttonText: data.buttonText,
          image: data.image,
          imageUrl: imageSrc,
          layout: data.layout || '1',
          backgroundColor: data.backgroundColor
        } as any;
        ctx.elements = [...ctx.elements];
      }
      
      // Schedule autosave
      ctx.scheduleAutosave(['/page/sections/hero_section']);
      return;
    }
    
    if (!selected) {
      this.logger.warn('⚠️ onHeroPanelChange: No element selected and hero panel not open, changes will not be saved');
      return;
    }

    // Update JSON mirror like in save (legacy path for non-hero elements)
    if (!ctx.json) ctx.json = {};

    // Ensure page.sections structure exists
    if (!ctx.json.page) ctx.json.page = {};
    if (!ctx.json.page.sections) ctx.json.page.sections = {};

    switch (selected.id) {
      case 'hero': {
        // Update in page.sections.hero_section (new structure)
        if (!ctx.json.page.sections.hero_section) {
          ctx.json.page.sections.hero_section = {};
        }
        const heroSection = ctx.json.page.sections.hero_section;
        // Preserve existing properties (layout, active, etc.) and only update content fields
        // Update title with new structure: title.label and title.style
        // Handle different title structures: string, old object (text_style), or new object
        const existingTitle = heroSection.title;
        if (existingTitle && typeof existingTitle === 'object' && existingTitle.label) {
          // Existing object structure - preserve and update
          heroSection.title = {
            ...existingTitle,
            label: data.heading,
            style: {
              // Remove old text_style if it exists, use new style properties
              fontWeight: data.titleStyle?.fontWeight || null,
              fontStyle: data.titleStyle?.fontStyle || null,
              textDecoration: data.titleStyle?.textDecoration || null,
              textAlign: data.titleStyle?.textAlign || null
            }
          };
        } else if (typeof existingTitle === 'string') {
          // Old string structure - convert to new structure
          heroSection.title = {
            label: data.heading,
            style: {
              fontWeight: data.titleStyle?.fontWeight || null,
              fontStyle: data.titleStyle?.fontStyle || null,
              textDecoration: data.titleStyle?.textDecoration || null,
              textAlign: data.titleStyle?.textAlign || null
            }
          };
        } else {
          // No existing title or invalid structure - create new
          heroSection.title = {
            label: data.heading,
            style: {
              fontWeight: data.titleStyle?.fontWeight || null,
              fontStyle: data.titleStyle?.fontStyle || null,
              textDecoration: data.titleStyle?.textDecoration || null,
              textAlign: data.titleStyle?.textAlign || null
            }
          };
        }
        heroSection.description = data.paragraph;
        heroSection.button = {
          ...(heroSection.button || {}),
          label: data.buttonText,
          link: data.buttonNavigation,
          style: heroSection.button?.style || {
            background: "#1E40AF",
            color: "#FFFFFF"
          }
        };
        const imageSrc = data.image?.src || data.imageUrl || '';
        const imageScaletype = data.image?.scaletype || data.imageFit || 'fit';
        heroSection.image = {
          ...(heroSection.image || {}),
          src: imageSrc,
          alt: data.image?.alt || heroSection.image?.alt || '',
          width: data.image?.width || heroSection.image?.width || '420px',
          height: data.image?.height || heroSection.image?.height || '320px',
          scaletype: imageScaletype
        };
        // Keep active state if it exists
        if (heroSection.active === undefined) {
          heroSection.active = true;
        }
        
        // Update layout - ensure layout object exists
        if (!heroSection.layout) {
          heroSection.layout = {
            type: 'layout_1',
            grid_columns: 2,
            image_alignment: "right",
            padding: "100px 120px"
          };
        }
        
        // Convert layout to proper format (layout_X)
        const layoutType = data.layout || 'layout_1';
        if (typeof layoutType === 'string') {
          // If it's already in layout_X format, use it
          if (layoutType.startsWith('layout_')) {
            heroSection.layout.type = layoutType as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
          } 
          // If it's just a number (1, 2, 3, etc.), convert to layout_X
          else if (/^[1-7]$/.test(layoutType)) {
            heroSection.layout.type = `layout_${layoutType}` as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
            // Update image_alignment based on layout number
            if (layoutType === '3') {
              heroSection.layout.image_alignment = "left";
            } else {
              heroSection.layout.image_alignment = "right";
            }
          }
          // Default to layout_1
          else {
            heroSection.layout.type = 'layout_1';
          }
        } else {
          heroSection.layout.type = 'layout_1';
        }
        
        // Update background_color in layout
            heroSection.layout.background_color = data.backgroundColor;
        
        // Update grid_columns and padding if not set
        if (!heroSection.layout.grid_columns) heroSection.layout.grid_columns = 2;
        if (!heroSection.layout.padding) heroSection.layout.padding = '100px 120px';
        
        // Update backward compatibility properties
        ctx.heroPanelData.imageUrl = imageSrc;
        ctx.heroPanelData.imageFit = imageScaletype;

        // reflect to preview immediately
        const heroElement = ctx.elements.find(e => e.id === 'hero');
        if (heroElement) {
          // Preserve existing content and only update changed fields
          heroElement.content = {
            ...heroElement.content,
            image: data.image || {
              src: '',
              alt: '',
              width: '420px',
              height: '320px',
              scaletype: 'fit'
            },
            title: data.heading,
            titleStyle: data.titleStyle || undefined,
            description: data.paragraph,
            buttonText: data.buttonText,
            imageUrl: imageSrc, // Legacy support
            layout: data.layout || '1',
            backgroundColor: data.backgroundColor
          } as any;

          // Force change detection by creating a new array reference
          ctx.elements = [...ctx.elements];
        }
        break;
      }
      case 'welcome': {
        // Update in page.sections.info_section (new structure)
        if (!ctx.json.page.sections.info_section) {
          ctx.json.page.sections.info_section = {};
        }
        const aboutSection = ctx.json.page.sections.info_section;
        aboutSection.title = data.heading;
        aboutSection.description = data.paragraph;
        aboutSection.button = {
          ...(aboutSection.button || {}),
          label: data.buttonText,
          link: data.buttonNavigation
        };
        aboutSection.image = {
          ...(aboutSection.image || {}),
          src: data.image?.src || '',
          alt: data.image?.alt || '',
          width: data.image?.width || '420px',
          height: data.image?.height || '320px',
          scaletype: data.image?.scaletype || 'fit'
        };
        // Keep active state if it exists
        if (aboutSection.active === undefined) {
          aboutSection.active = true;
        }
        // Store layout value (1, 2, or 3)
        aboutSection.layout_type = data.layout || '1';

        // reflect to preview immediately
        const welcomeElement = ctx.elements.find(e => e.id === 'welcome');
        if (welcomeElement) {
          // Preserve existing content and only update changed fields
          welcomeElement.content = {
            ...welcomeElement.content,
            title: data.heading,
            description: data.paragraph,
            buttonText: data.buttonText,
            imageUrl: data.image?.src || '', // Legacy support
            image: data.image || {
              src: '',
              alt: '',
              width: '420px',
              height: '320px',
              scaletype: 'fit'
            },
            layout: data.layout || '1'
          } as any;

          // Force change detection by creating a new array reference
          ctx.elements = [...ctx.elements];
        }
        break;
      }
      case 'services': {
        // Services section is no longer used - removed
        break;
      }
    }
    const paths: string[] = [];
    if (selected.id === 'hero') paths.push('/hero');
    if (selected.id === 'welcome') paths.push('/sections/welcome');
    if (selected.id === 'services') paths.push('/services');
    
    // 🔥 FIX: Record state AFTER all data is updated (not before!)
    ctx.recordStateChange('Hero section updated');
    
    ctx.scheduleAutosave(paths);
  }
}